import { ProfileService } from './../profile.service';
import { DeleteProfileAttachmentDialogComponent } from './../delete-profile-attachment-dialog/delete-profile-attachment-dialog.component';
import { Subscription } from 'rxjs';
import { AddProfileAttachmentDialogComponent } from './../add-profile-attachment-dialog/add-profile-attachment-dialog.component';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Attachment } from './../../../shared-components/models/attachment.model';
import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { TylerMatTableDataSource } from '../../../shared-components/angular-material/extended.class';
import { ProfileAttachment } from '../profile-attachment.model';
import { System } from '../../System';
import { Profile } from '../profile.model';
import { LoginService } from '../../../shared-modules/login/login.service';
import { AttachmentsMenuDialogComponent } from '../attachments-menu-dialog/attachments-menu-dialog.component';
/**
 * This component is used for creating a table of profile attachments.
 *
 * @input profile: a selected profile
 * @input userCanEdit: set this to true if the user can edit the profiles. False by default.
 * @input attachmentChanged: this is an event emitter signalling if the attachment have changed.
 *      If your attachment might change, be sure to supply this event emitter.
 *      Emit from that event emitter if and when your attachment change.
 */
@Component({
  selector: 'attachment-table',
  templateUrl: './attachment-table.component.html',
  styleUrls: ['./attachment-table.component.css']
})
export class AttachmentTableComponent implements OnInit, OnDestroy {

  // Input Properties
  @Input() profile: Profile;
  @Input() userCanEdit = false;
  @Input() attachmentChanged: EventEmitter<any>;

  // Local Properties
  tableData: TylerMatTableDataSource;
  columns = ['file', 'uploaded', 'uploaded by', 'visible to parents students', 'actions'];
  subscriptions: Subscription[] = [];
  V10: boolean;
  attachments: Attachment[] = [];

  constructor(
    private dialogBox: MatDialogRef<AttachmentsMenuDialogComponent>,
    private dialog: MatDialog,
    private profileService: ProfileService,
    private loginService: LoginService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.V10 = this.loginService.LoginSettings.V10;
    if (!this.userCanEdit) {
      this.columns.splice(this.columns.indexOf('actions'), 1);
    }
    this.loadTable();

    if (this.attachmentChanged) {
      this.subscriptions.push(
          this.attachmentChanged.subscribe(() => {
            this.loadTable();
          }),
      );
  }
  }

  private loadTable() {
    this.attachments = this.profile ? this.profile.ProfileAttachments : [];
    this.tableData = new TylerMatTableDataSource(this.attachments);
    this.changeDetector.detectChanges();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }

  // Methods
  downloadAttachment(attachment: ProfileAttachment) {
    // PRE: an attachment to view
    // POST: downloads the attachment to the user
    console.log(attachment);
    this.subscriptions.push(
      this.profileService.getAttachmentFile(attachment.FileID).subscribe(result => {
        console.log(result);
        const filename = this.V10 ? result.MyFileName + '.' + result.FileExtension : result.MyFileName;
        System.saveFile(result.MyFile, result.FileType, filename);
      })
    );
  }

  editAttachment(attachment: ProfileAttachment) {
    // PRE: an attachment to edit
    // POST: opens the dialog for editing the attachment
    const changedAttachment = Object.assign(new ProfileAttachment, attachment);
    changedAttachment.ContentID = this.profile.ProfileContentId;
    const dialogBox = this.dialog.open(AddProfileAttachmentDialogComponent, {
      data: {
        attachment: changedAttachment
      },
      disableClose: true
    });
    this.subscriptions.push(
      dialogBox.afterClosed().subscribe(() => {
        if (dialogBox.componentInstance.saved) {
          Object.assign(attachment, changedAttachment);
        }
      })
    );
  }

  deleteAttachment(attachment: ProfileAttachment) {
    // PRE: an attachment to delete
    // POST: opens the dialog for deleting the attachment
    attachment.ContentID = this.profile.ProfileContentId;
    const dialogBox = this.dialog.open(DeleteProfileAttachmentDialogComponent, {
      data: {
        attachment: attachment
      },
      disableClose: true
    });
    this.subscriptions.push(
      dialogBox.afterClosed().subscribe(() => {
        if (dialogBox.componentInstance.deleted) {
          this.attachments.splice(this.attachments.indexOf(attachment), 1);
          this.tableData = new TylerMatTableDataSource(this.attachments);
        }
      })
    );
  }
}
